import { FunctionComponent, useEffect } from 'react'
import { ActiveUserJourney, syncActiveJourney, useAppDispatch } from '../../state'
import { UserJourney } from './types'
import { useUserJourneyContext } from './UserJourneyContext'

const toActiveJourney = (journey: UserJourney | undefined): ActiveUserJourney | undefined =>
  journey === undefined
    ? undefined
    : {
        name: journey.name,
        journeyId: journey.id,
        createdAt: journey.created,
        variant: journey.variant,
        tags: journey.tags,
        frustrations: journey.frustrations,
        frictions: journey.frictions,
      }

/**
 * Mirrors the active journey from the userJourney integration context into the
 * `userJourney` Redux slice, so thunks and other non-React code can read the journey
 * correlation id. Must be mounted inside both the Redux Provider and UserJourneyProvider.
 */
export const UserJourneyStateSync: FunctionComponent = () => {
  const { active, subscribe } = useUserJourneyContext()
  const dispatch = useAppDispatch()

  // Journey dispatches notify subscribers synchronously (useUserJourney.ts), so the
  // mirror updates in the same tick as startJourney/addTag/completeJourney — without
  // this, a thunk dispatched in the same handler would read the previous journey id,
  // breaking the slice's contract above.
  useEffect(
    () =>
      subscribe(({ state }) => {
        dispatch(syncActiveJourney(toActiveJourney(state.active !== undefined ? state.journeys[state.active] : undefined)))
      }),
    [subscribe, dispatch]
  )

  // Mount-time sync: covers a journey already in flight when this component (re)mounts.
  // After live dispatches it re-writes the same value, which is idempotent.
  useEffect(() => {
    dispatch(syncActiveJourney(toActiveJourney(active)))
  }, [active, dispatch])

  return null
}
